import {
  Component,
  DoCheck,
  OnInit,
  OnDestroy,
  Input,
  ViewChild,
  HostBinding,
  inject,
} from '@angular/core';
import { DxTreeViewTypes } from 'devextreme-angular/ui/tree-view';
import { DxDrawerComponent, DxDrawerModule, DxDrawerTypes } from 'devextreme-angular/ui/drawer';
import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd, Event } from '@angular/router';
import { Subscription } from 'rxjs';

import { TranslateModule } from '@ngx-translate/core';
import { ScreenService, AppInfoService } from '../../services';
import { SideNavigationMenuComponent, AppHeaderComponent, AppFooterComponent } from '../../components';

@Component({
  selector: 'app-side-nav-outer-toolbar',
  templateUrl: './side-nav-outer-toolbar.component.html',
  styleUrls: ['./side-nav-outer-toolbar.component.scss'],
  imports: [
    RouterModule,
    SideNavigationMenuComponent,
    DxDrawerModule,
    AppHeaderComponent,
    CommonModule,
    AppFooterComponent,
    TranslateModule,
  ],
})
export class SideNavOuterToolbarComponent implements OnInit, OnDestroy, DoCheck {
  @ViewChild(DxScrollViewComponent, { static: true }) scrollView!: DxScrollViewComponent;
  @ViewChild(DxDrawerComponent) drawer?: DxDrawerComponent;

  @Input()
  title!: string;

  private screen = inject(ScreenService);

  private router = inject(Router);

  protected appInfo = inject(AppInfoService);

  selectedRoute = '';

  menuOpened!: boolean;

  temporaryMenuOpened = false;

  menuMode: DxDrawerTypes.OpenedStateMode = 'shrink';

  menuRevealMode: DxDrawerTypes.RevealMode = 'expand';

  minMenuSize = 0;

  shaderEnabled = false;

  // === Resize del sidebar ===
  // Ancho persistido en localStorage para desktop. Default 250, rango 200..500.
  // En mobile/tablet se ignora el guardado y se calcula del viewport (no overflow).
  private readonly STORAGE_W = 'crm.sidebar.width';
  private desktopSidebarSize = this.readSidebarWidth();
  sidebarMaxSize = this.desktopSidebarSize;
  private resizing = false;
  private resizeStartX = 0;
  private resizeStartW = 0;

  // CSS vars que el SCSS consume: el ancho actual y el "shift negativo" para
  // contrarrestar el offset inline que DevExtreme aplica al .dx-overlay-wrapper
  // en mobile/tablet (usado en media query, ver styles). Empíricamente el offset
  // que mete DevExtreme es ~2x el ancho del panel, así que el shift es 2*width.
  @HostBinding('style.--crm-sidebar-w') get sidebarWidthVar() {
    return `${this.sidebarMaxSize}px`;
  }
  @HostBinding('style.--crm-sidebar-shift') get sidebarShiftVar() {
    return `-${this.sidebarMaxSize * 2}px`;
  }

  routerSubscription: Subscription;

  screenSubscription: Subscription;

  constructor() {
    this.routerSubscription = this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.selectedRoute = event.urlAfterRedirects.split('?')[0];
      }
    });
  }

  ngOnInit() {
    this.menuOpened = this.screen.sizes['screen-large'];

    this.screenSubscription = this.screen.changed.subscribe(() => this.updateDrawer());

    this.updateDrawer();

    // Drag global handlers (los enganchamos en window para no perder el drag al salir del handle).
    window.addEventListener('mousemove', this.onWindowMouseMove);
    window.addEventListener('mouseup', this.onWindowMouseUp);
    window.addEventListener('touchmove', this.onWindowMouseMove, { passive: false });
    window.addEventListener('touchend', this.onWindowMouseUp);
    // Recalcular ancho al rotar / cambiar viewport (cubre rotation en mobile).
    window.addEventListener('resize', this.onWindowResize);
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
    this.screenSubscription.unsubscribe();
    window.removeEventListener('mousemove', this.onWindowMouseMove);
    window.removeEventListener('mouseup', this.onWindowMouseUp);
    window.removeEventListener('touchmove', this.onWindowMouseMove);
    window.removeEventListener('touchend', this.onWindowMouseUp);
    window.removeEventListener('resize', this.onWindowResize);
    // Limpia el body class para no dejar estado fantasma si el layout se destruye.
    document.body.classList.remove('crm-sidebar-compact');
  }

  /**
   * Sincroniza una clase global `body.crm-sidebar-compact` con el estado del menu.
   * El boton hamburguesa cambia `menuOpened` de manera reactiva pero el header
   * lee el flag via CSS (`:host-context(body.crm-sidebar-compact)`) en lugar
   * de Input + HostBinding — empiricamente el HostBinding-getter no refrescaba
   * en algunos paths de cambio de menuOpened en desktop.
   *
   * `ngDoCheck` corre por cada CD del componente; tocar el body class solo si
   * cambia el flag deseado evita layout thrash.
   */
  private bodyCompactApplied = false;
  ngDoCheck(): void {
    const wantCompact = !this.menuOpened;
    if (wantCompact !== this.bodyCompactApplied) {
      this.bodyCompactApplied = wantCompact;
      document.body.classList.toggle('crm-sidebar-compact', wantCompact);
    }
  }

  private onWindowResize = (): void => {
    this.recomputeSidebarSize();
    // DevExtreme deja inline-styles residuales en `.dx-drawer-panel-content` cuando el
    // viewport cruza un breakpoint y `menuMode` flipea entre 'shrink' y 'overlap'
    // (tipico al abrir/cerrar F12 docked-side). Sin repintar, el .dx-drawer-content
    // queda con el ancho del modo viejo y la grilla mide mal. Esperamos un frame
    // para que Angular aplique el binding nuevo y despues repintamos.
    requestAnimationFrame(() => this.drawer?.instance?.repaint());
  };

  updateDrawer() {
    const isXSmall = this.screen.sizes['screen-x-small'];
    const isSmall = this.screen.sizes['screen-small'];
    const isLarge = this.screen.sizes['screen-large'];
    const prevMode = this.menuMode;
    const mobileOrTablet = isXSmall || isSmall;

    this.menuMode = isLarge ? 'shrink' : 'overlap';
    this.menuRevealMode = mobileOrTablet ? 'slide' : 'expand';
    // En mobile/tablet el drawer es overlap: minSize=0 para que se oculte
    // completamente al cerrarse (sin rail compact). En desktop dejamos 48px
    // de rail visible para que el usuario pueda navegar por iconos.
    this.minMenuSize = mobileOrTablet ? 0 : 48;
    this.shaderEnabled = !isLarge;

    // En mobile/tablet el drawer debe arrancar cerrado, sin importar el estado
    // anterior. Cubre el caso de rotar desktop → mobile (o resize del viewport)
    // donde menuOpened quedó en true y bloqueaba el contenido.
    if (mobileOrTablet && this.menuOpened) {
      this.menuOpened = false;
    }

    this.recomputeSidebarSize();

    // Si cambia el modo, forzamos repaint del drawer para que limpie inline styles
    // del modo anterior. Sin esto, ir 'shrink' → 'overlap' → 'shrink' deja el
    // contenido con un ancho incorrecto.
    if (prevMode && prevMode !== this.menuMode) {
      requestAnimationFrame(() => this.drawer?.instance?.repaint());
    }
  }

  // En mobile/tablet el ancho guardado para desktop puede exceder el viewport
  // (un usuario que arrastró el handle a 400px lo veía pisando la pantalla del cel).
  // Acá clampeamos a 85% del viewport con un techo razonable.
  private recomputeSidebarSize(): void {
    const isXSmall = this.screen.sizes['screen-x-small'];
    const isSmall = this.screen.sizes['screen-small'];
    if (isXSmall || isSmall) {
      const vw = typeof window !== 'undefined' ? window.innerWidth : 360;
      this.sidebarMaxSize = Math.min(this.desktopSidebarSize, Math.floor(vw * 0.85), 320);
    } else {
      this.sidebarMaxSize = this.desktopSidebarSize;
    }
  }

  private isMobileOrTablet(): boolean {
    return this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'];
  }

  get hideMenuAfterNavigation() {
    return this.menuMode === 'overlap' || this.temporaryMenuOpened;
  }

  get showMenuAfterClick() {
    return !this.menuOpened;
  }

  navigationChanged(event: DxTreeViewTypes.ItemClickEvent) {
    const itemData = event.itemData as any;
    const path = itemData?.path as string | undefined;
    const hasChildren = Array.isArray(itemData?.items) && itemData.items.length > 0;
    const pointerEvent = event.event;
    // Sin el dxclick global el sidebar ya no se auto-abre antes de que llegue
    // el onItemClick — `menuOpened` refleja el estado real al momento del click.
    const wasCompact = !this.menuOpened;

    if (path) {
      // Item con path: navega siempre (en compact o expandido). Si estabamos en
      // compact, NO dejamos el sidebar abierto despues de navegar — se siente
      // como un atajo: click en icono → cambio de pagina, sidebar vuelve al rail.
      if (event.node?.selected) {
        pointerEvent?.preventDefault();
      } else {
        const [pure, qs] = path.split('?');
        const queryParams: Record<string, string> = {};
        if (qs) {
          for (const pair of qs.split('&')) {
            const [k, v] = pair.split('=');
            if (k) queryParams[decodeURIComponent(k)] = decodeURIComponent(v ?? '');
          }
        }
        this.router.navigate([pure], qs ? { queryParams } : undefined);
      }

      // Tras navegar:
      //  - Si estabamos en compact, volvemos a compact (cancelamos el openMenu
      //    automatico del dxclick global).
      //  - Si estabamos en overlap (mobile/tablet), cerramos para que el
      //    contenido sea visible.
      //  - Si estabamos en expanded desktop, dejamos abierto (comportamiento
      //    historico — el usuario tiene espacio para seguir navegando).
      if (wasCompact || this.hideMenuAfterNavigation) {
        this.temporaryMenuOpened = false;
        this.menuOpened = false;
        pointerEvent?.stopPropagation();
      }
    } else if (hasChildren && wasCompact) {
      // Grupo (sin path) clickeado en compact: abrir el sidebar y expandir
      // el grupo para que vea sus hijos. Es la unica forma de "entrar" a un
      // grupo desde el rail. La rama L1 se expande sola al abrir gracias al
      // setter `compactMode = false → expandItem(this._selectedItem)` — pero
      // el grupo clickeado es DISTINTO al selected, asi que lo expandimos
      // manualmente.
      this.temporaryMenuOpened = true;
      this.menuOpened = true;
      // El dx-treeview ya tiene el item seleccionado por el click; el expand
      // viene en el siguiente tick para que el setter de compactMode termine.
      requestAnimationFrame(() => {
        const key = (event.node as any)?.key;
        if (key) {
          try { (event.component as any)?.expandItem?.(key); } catch { /* noop */ }
        }
      });
    } else {
      pointerEvent?.preventDefault();
    }
  }

  navigationClick() {
    // El dxclick global del sidebar dispara este handler. Antes expandiamos
    // SIEMPRE en compact, pero eso interferia con el click directo a navegar.
    // Ahora `navigationChanged` decide si abrir/cerrar segun el item, y este
    // metodo queda solo para clicks fuera de items (header del menu, espacios
    // vacios). Para que esos abran el sidebar igual, dejamos la logica intacta:
    // si llega aca con menu cerrado, abrimos.
    if (this.showMenuAfterClick) {
      this.temporaryMenuOpened = true;
      this.menuOpened = true;
    }
  }

  // === Resize handle handlers ===
  startResize(e: MouseEvent | TouchEvent): void {
    // En mobile/tablet el sidebar es overlay y se adapta al viewport: no se puede arrastrar.
    if (this.isMobileOrTablet()) return;
    this.resizing = true;
    this.resizeStartX = this.getClientX(e);
    this.resizeStartW = this.desktopSidebarSize;
    document.body.classList.add('crm-resizing-sidebar');
    e.preventDefault();
  }

  onWindowMouseMove = (e: MouseEvent | TouchEvent): void => {
    if (!this.resizing) return;
    const delta = this.getClientX(e) - this.resizeStartX;
    const next = Math.max(200, Math.min(500, this.resizeStartW + delta));
    this.desktopSidebarSize = next;
    this.sidebarMaxSize = next;
  };

  onWindowMouseUp = (): void => {
    if (!this.resizing) return;
    this.resizing = false;
    document.body.classList.remove('crm-resizing-sidebar');
    try {
      localStorage.setItem(this.STORAGE_W, String(this.desktopSidebarSize));
    } catch { /* ignore */ }
  };

  private getClientX(e: MouseEvent | TouchEvent): number {
    return 'touches' in e ? e.touches[0]?.clientX ?? 0 : e.clientX;
  }

  private readSidebarWidth(): number {
    const raw = Number(localStorage.getItem('crm.sidebar.width'));
    if (!Number.isFinite(raw) || raw < 200 || raw > 500) return 250;
    return raw;
  }
}
